import { StudentRequirementsTutorsProcessTableProps } from "../types";
import {
  Button,
  DatePicker,
  Table,
  TableColumnProps,
  Tooltip,
  message,
} from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import { renderBoolean, renderDistanceLinkGen } from "../../table-utils";
import { useEffect, useState } from "react";
import axios from "../../../axios";
import {
  STUDENT_DEALINGS_STATUS_COLORS,
  TUTOR_PROCESS_STATUS,
} from "../../constants";
import TheDateTimePicker from "../../TheDatePicker";
import dayjs from "dayjs";
import { StatusSwitch } from "../../StatusSwitch";
import "../tutor-status-rows.css";
import TutorCommentsPopup from "../TutorCommentsPopup";
import { adminEditDealState } from "../../../states/userRole";
import { useAtom } from "jotai";

// import { render } from "@testing-library/react";
type ProcessTutorData = {
  id: number;
  status: string;
  tutor_id: number;
  distance: number;
  demo_date: string;
  confirmed_tuition_date: string;
  start_date: string;
  gender: string;
  phone: string;
  tutor_name: string;
  dealings_id: number;
  tutor_status: string;
  shortlist_status: string;
  is_verified: boolean;
  expertise: string;
  is_applied: boolean;
  is_shortlisted: boolean;
  match_percentage: string;
  process_status: string;
  location: string;
  comment_count: number;
  tutor_rating: number;
};

export function StudentRequirementsTutorsProcessTable(
  props: StudentRequirementsTutorsProcessTableProps
) {
  const { studentDetails, setStudentDealStatus } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [tutorsList, setTutorsList] = useState<Array<ProcessTutorData>>([]);
  const [metaInfo, setMetaInfo] = useState({ total: 0 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [demoDate, setDemoDate] = useState<dayjs.Dayjs | null>(null);
  const [state, setState] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [canAdminEditDeal, _setCanAdminEditDeal] = useAtom(adminEditDealState);

  useEffect(() => {
    fetchTutorRecoList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, studentDetails.id, state]);

  async function fetchTutorRecoList() {
    setLoading(true);
    try {
      const response = await axios.get(
        `/v1/cre/student/recommendation/process`,
        {
          params: {
            dealings_id: studentDetails.id,
            page: 1,
            limit: 25,
          },
        }
      );
      setTutorsList(response.data.data.data);
      setMetaInfo({ total: response.data.data.meta.total });
    } catch (error) {
      console.error(
        `Error fetching comments for record ${studentDetails.id}:`,
        error
      );
    } finally {
      setLoading(false);
    }
  }

  async function updateTutorStatus(
    field: "demo_date" | "status" | "confirmed_tuition_date" | "start_date",
    value: string | undefined,
    record: Record<string, unknown>
  ) {
    if (!value) return;
    try {
      setLoading(true);
      const { data } = await axios.post(
        `/v1/cre/student/process/update-status`,
        {
          dealings_id: studentDetails.id,
          shortlist_id: record.id,
          status: record.status,
          [field]: value,
        }
      );
      if (data.success) {
        setStudentDealStatus(
          data.data.dealStatus as keyof typeof STUDENT_DEALINGS_STATUS_COLORS
        );
        message.success("Successfully updated Process status");
      } else {
        message.error(data.message ?? "Somethign went wrong");
      }
      setState(!state);
    } catch (error) {
      console.log("Error updating tutor status:", error);
    } finally {
      setLoading(false);
    }
  }

  const _columns: TableColumnProps<ProcessTutorData>[] = [
    {
      title: "Tutor Name",
      dataIndex: "tutor_name",
      key: "tutor_name",
      width: 180,
      align: "center",
      render: (_, record) => {
        return (
          <Tooltip placement="topLeft" title={record.tutor_name}>
            <div
              style={{
                padding: "5px",
              }}
            >
              {record.tutor_name}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Phone no.",
      dataIndex: "phone",
      width: 150,
      key: "phone",
      align: "center",
    },
    {
      title: "Verified",
      dataIndex: "is_verified",
      key: "is_verified",
      render: renderBoolean,
      align: "center",
      width: 80,
      onCell: (record: ProcessTutorData) => ({
        style: {
          color: record.is_verified ? "#ffffff" : "#000000",
          backgroundColor: record.is_verified ? "#1bb83d" : "#ffffff",
        },
      }),
    },
    {
      title: "Applied",
      dataIndex: "is_applied",
      key: "is_applied",
      render: renderBoolean,
      align: "center",
      width: 80,
      onCell: (record: ProcessTutorData) => ({
        style: {
          color: record.is_applied ? "#ffffff" : "#000000",
          backgroundColor: record.is_applied ? "#ddb42d" : "#ffffff",
        },
      }),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      align: "center",
      width: 70,
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      render: (cellValue: string) => <span>{cellValue} km</span>,
      width: 70,
    },
    {
      title: "Google Distance",
      dataIndex: "location",
      key: "location",
      render: renderDistanceLinkGen,
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (
        cellValue: keyof typeof TUTOR_PROCESS_STATUS,
        record: ProcessTutorData
      ) => (
        <StatusSwitch
          // disabled={!canAdminEditDeal}
          value={cellValue}
          options={TUTOR_PROCESS_STATUS}
          onChange={(e) => {
            updateTutorStatus("status", e.target.value, record);
          }}
        />
      ),
      width: 150,
      align: "center",
    },
    {
      title: "Demo Date & Timing",
      dataIndex: "demo_date",
      key: "demo_date",
      width: 200,
      align: "center",
      render: function (
        cellValue: dayjs.Dayjs | null | undefined,
        record: Record<string, unknown>
      ) {
        let parsedDate = null;
        if (dayjs(cellValue).isValid()) {
          parsedDate = dayjs(cellValue);
        }
        return (
          <TheDateTimePicker
            value={parsedDate}
            onChange={(val: dayjs.Dayjs | null) =>
              updateTutorStatus("demo_date", val?.toISOString(), record)
            }
          />
        );
      },
    },
    {
      title: "Confirmed Tuition Date",
      dataIndex: "confirmed_tuition_date",
      key: "confirmed_tuition_date",
      width: 200,
      align: "center",
      render: function (
        cellValue: dayjs.Dayjs | null | undefined,
        record: Record<string, unknown>
      ) {
        let parsedDate = null;
        if (dayjs(cellValue).isValid()) {
          parsedDate = dayjs(cellValue);
        }
        return (
          <DatePicker
            value={parsedDate}
            variant="borderless"
            onChange={(val: dayjs.Dayjs | null) =>
              updateTutorStatus(
                "confirmed_tuition_date",
                val?.toISOString(),
                record
              )
            }
            allowClear={false}
          />
        );
      },
    },
    {
      title: "Start Date & Timing",
      dataIndex: "start_date",
      key: "start_date",
      width: 200,
      align: "center",
      render: function (
        cellValue: dayjs.Dayjs | null | undefined,
        record: Record<string, unknown>
      ) {
        let parsedDate = null;
        if (dayjs(cellValue).isValid()) {
          parsedDate = dayjs(cellValue);
        }
        return (
          <TheDateTimePicker
            value={parsedDate}
            onChange={(val: dayjs.Dayjs | null) =>
              updateTutorStatus("start_date", val?.toISOString(), record)
            }
          />
        );
      },
      // ellipsis: true,
    },
    {
      title: "Expertise",
      dataIndex: "expertise",
      key: "expertise",
      width: 250,
      ellipsis: true,
      render: (_, record) => {
        return (
          <Tooltip placement="topLeft" title={record.expertise}>
            <div
              style={{
                padding: "5px",
              }}
            >
              {record.expertise}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Match Percentage",
      dataIndex: "match_percentage",
      key: "match_percentage",
      width: 100,
      render: (cellValue: string) => <span>{cellValue} %</span>,
    },
    {
      title: "Rating",
      dataIndex: "tutor_rating",
      key: "tutor_rating",
      width: 70,
      align: "center",
      render: (cellValue: number) =>
        cellValue > 0 ? (
          <span style={{ color: "#faad14" }}>
            {"★".repeat(Math.round(cellValue))}
            <span style={{ color: "#d9d9d9" }}>
              {"★".repeat(5 - Math.round(cellValue))}
            </span>
          </span>
        ) : (
          <span style={{ color: "#d9d9d9" }}>★★★★★</span>
        ),
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      align: "center",
      width: 100,
      render: (cellValue: string, record: ProcessTutorData) => (
        <TutorCommentsPopup
          cellValue={cellValue}
          data={{
            shortlist_id: record.id,
            comment_count: record.comment_count,
          }}
        />
      ),
    },
    {
      title: "Tutor Details",
      dataIndex: "tutor_id",
      key: "tutor_id",
      align: "center",
      width: 100,
      render: (cellValue: string) => (
        <Button
          className="tail-mx-auto"
          type="text"
          iconPosition="end"
          icon={<InfoCircleFilled className="tail-text-blue-500" />}
          href={`/tutor/${cellValue}`}
          target="_blank"
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <Table
      dataSource={tutorsList}
      loading={loading}
      pagination={{
        total: metaInfo.total,
        pageSize: 25,
        onChange: (page, pageSize) => setPage(page),
        showSizeChanger: false,
        current: page,
      }}
      bordered={true}
      onChange={(pagination, filters, sorter, extra) => {}}
      tableLayout="fixed"
      scroll={{ x: "100%" }}
      rowClassName={(record) => {
        const status = (record.tutor_status || "").toLowerCase();
        if (status === "blocked" || status === "block") return "tutor-row-blocked";
        if (status === "inactive") return "tutor-row-inactive";
        if (status === "warning") return "tutor-row-warning";
        return "";
      }}
      columns={_columns}
    />
  );
}
